import { useMemo } from "react";
import { Icon } from "@zapier/zinnia-icons";
import { BetaDataTableCell as Cell, Link, Text } from "@zapier/design-system";
import { getFolderUrl, getProjectUrl } from "@zapier/utils";
import { useCurrentUser } from "@zapier/client";
import { TooltipText } from "../TooltipText";

import styles from "./LocationCell.module.css";

interface LocationProps {
  folderId?: string | null;
  folderName?: string;
  /** Unlinked cells render from a name alone — no id, and so no href. */
  isLinked?: boolean;
  isSharedFolder?: boolean;
  ownerId?: number;
  projectId?: string | null;
  projectName?: string;
  className?: string;
  usePersonalFolder?: boolean;
}

const Location = ({
  folderId,
  projectId,
  folderName,
  projectName,
  isLinked = true,
  isSharedFolder,
  ownerId,
  className,
  usePersonalFolder,
}: LocationProps) => {
  const { user, isLoading: isUserLoading } = useCurrentUser();

  const isLoading = useMemo(
    () => (folderId && !folderName) || (projectId && !projectName),
    [folderId, folderName, projectId, projectName],
  );

  const isProject = useMemo(
    () => !!projectId && !!projectName,
    [projectId, projectName],
  );

  // Zaps may be in a folder OR project, but other types of assets may be in neither.
  const locationHref = useMemo(
    () =>
      isProject
        ? getProjectUrl(projectId)
        : // If the current user is the owner of the folder, we don't need to pass the ownerId
          getFolderUrl(folderId, user?.id === ownerId ? undefined : ownerId),
    [isProject, projectId, folderId, user?.id, ownerId],
  );

  const label = projectName || folderName;

  const isVisible = isLinked
    ? !!(folderId || projectId || usePersonalFolder) &&
      !isLoading &&
      !isUserLoading
    : !!label;

  const content = (
    <span className={styles.location}>
      <Icon
        name={isProject ? "navProjects" : isSharedFolder ? "team" : "navFolder"}
        size={18}
        color="GrayWarm6"
        className={styles.icon}
        aria-hidden={true}
      />
      <div className={styles.tooltipContainer}>
        <TooltipText
          id={`location-${label}`}
          label={label || ""}
          placement="bottom"
        >
          <Text type="Body1" color="TextDefault">
            {label}
          </Text>
        </TooltipText>
      </div>
    </span>
  );

  return (
    <Cell className={className}>
      {isVisible &&
        (isLinked ? (
          <Link href={locationHref} className={styles.locationLink}>
            {content}
          </Link>
        ) : (
          content
        ))}
    </Cell>
  );
};

export default Location;
